import { cityKey, NO_COUNTRY } from '../../data/pre-event-city-key';
import type { FilterTagLabels } from './filter-tags';
import type { CatalogueUniverses } from './match';
import type { ApiPreEvent, IndexedPreEvent } from './parse';
import { languageName, regionName } from './wall-clock';

export type FilterOption = {
  value: string;
  label: string;
};

export type CityLeaf = {
  key: string;
  city: string;
  search: string;
};

export type CountryBranch = {
  country: string;
  domKey: string;
  label: string;
  search: string;
  cities: CityLeaf[];
};

export type CatalogueCopy = {
  endpoint: string;
  lang: string;
  and: string;
  from: string;
  to: string;
  at: string;
  datePrefix: string;
  remove: string;
  dateWeek: string;
  dateMonth: string;
  dateMonths: string;
  noCountry: string;
};

export type CatalogueTeaser = {
  id: number;
  organisationName: string;
  name: string;
  when: string;
  startDate: string;
  endDate: string;
  placeLine: string;
  showFlag: boolean;
  flagAlt: string;
  attendance: string;
  attendanceType: string[];
  access: string;
  price: string;
  descriptionHtml: string;
  coverUrl: string;
  coverAlt: string;
  ctaUrl: string;
  ctaLabel: string;
  contactEmail: string;
  contactLabel: string;
  heading: string;
};

export const parseCopy = (raw: string): CatalogueCopy | undefined => {
  try {
    const parsed: unknown = JSON.parse(raw);
    if (!isRecord(parsed)) {
      return undefined;
    }

    const copy = {
      endpoint: parsed.endpoint,
      lang: parsed.lang,
      and: parsed.and,
      from: parsed.from,
      to: parsed.to,
      at: parsed.at,
      datePrefix: parsed.datePrefix,
      remove: parsed.remove,
      dateWeek: parsed.dateWeek,
      dateMonth: parsed.dateMonth,
      dateMonths: parsed.dateMonths,
      noCountry: parsed.noCountry,
    };

    return Object.values(copy).every((value) => typeof value === 'string')
      ? (copy as CatalogueCopy)
      : undefined;
  } catch {
    return undefined;
  }
};

export const buildUniverses = (events: IndexedPreEvent[]): CatalogueUniverses => ({
  types: optionValues(events.flatMap((item) => item.event.types)),
  attendance: optionValues(events.flatMap((item) => item.event.attendance)),
  access: optionValues(events.flatMap((item) => item.event.access == null ? [] : [item.event.access])),
  languages: [
    ...new Set(
      events.flatMap((item) => item.event.language == null ? [] : [item.event.language]),
    ),
  ].sort(),
});

export const buildOptions = (
  events: IndexedPreEvent[],
  lang: string,
): {
  themes: FilterOption[];
  types: FilterOption[];
  attendance: FilterOption[];
  access: FilterOption[];
  languages: FilterOption[];
} => ({
  themes: termOptions(events.flatMap((item) => item.event.theme == null ? [] : [item.event.theme]), lang),
  types: termOptions(events.flatMap((item) => item.event.types), lang),
  attendance: termOptions(events.flatMap((item) => item.event.attendance), lang),
  access: termOptions(events.flatMap((item) => item.event.access == null ? [] : [item.event.access]), lang),
  languages: buildUniverses(events).languages.map((code) => ({
    value: code,
    label: languageName(code, lang),
  })).sort((a, b) => a.label.localeCompare(b.label, lang)),
});

export const buildCityTree = (
  events: IndexedPreEvent[],
  lang: string,
  noCountry: string,
): CountryBranch[] => {
  const byCountry = new Map<string, Set<string>>();

  for (const item of events) {
    const city = item.event.city ?? '';
    if (city === '') {
      continue;
    }

    const country = item.event.countryCode === '' ? NO_COUNTRY : item.event.countryCode;
    const cities = byCountry.get(country) ?? new Set<string>();
    cities.add(city);
    byCountry.set(country, cities);
  }

  const branches = [...byCountry.entries()].map(([country, cities]) => {
    const label = country === NO_COUNTRY ? noCountry : regionName(country, lang);
    const leaves = [...cities]
      .sort((a, b) => a.localeCompare(b, lang))
      .map((city) => ({
        key: cityKey(country, city),
        city,
        search: `${label} ${city}`,
      }));

    return {
      country,
      domKey: country === '' ? 'none' : country,
      label,
      search: [label, ...leaves.map((leaf) => leaf.city)].join(' '),
      cities: leaves,
    };
  });

  const named = branches.filter((branch) => branch.country !== NO_COUNTRY);
  const unnamed = branches.filter((branch) => branch.country === NO_COUNTRY);
  named.sort((a, b) => a.label.localeCompare(b.label, lang));
  return [...named, ...unnamed];
};

export const cityTreeKeys = (tree: CountryBranch[]): Record<string, string[]> =>
  Object.fromEntries(tree.map((branch) => [branch.country, branch.cities.map((leaf) => leaf.key)]));

export const tagLabelsFor = (
  events: IndexedPreEvent[],
  tree: CountryBranch[],
  copy: CatalogueCopy,
): FilterTagLabels => {
  const options = buildOptions(events, copy.lang);

  return {
    datePrefix: copy.datePrefix,
    remove: copy.remove,
    theme: Object.fromEntries(options.themes.map((option) => [option.value, option.label])),
    type: Object.fromEntries(options.types.map((option) => [option.value, option.label])),
    attendance: Object.fromEntries(options.attendance.map((option) => [option.value, option.label])),
    access: Object.fromEntries(options.access.map((option) => [option.value, option.label])),
    language: Object.fromEntries(options.languages.map((option) => [option.value, option.label])),
    date: {
      week: copy.dateWeek,
      month: copy.dateMonth,
      months: copy.dateMonths,
    },
    country: Object.fromEntries(tree.map((branch) => [branch.country, branch.label])),
    city: Object.fromEntries(tree.flatMap((branch) => branch.cities.map((leaf) => [leaf.key, leaf.city]))),
  };
};

export const toTeaser = (
  event: ApiPreEvent,
  when: string,
  heading: string,
  lang: string,
  and: string,
): CatalogueTeaser => {
  const countryName = regionName(event.countryCode, lang);
  const placeLine = [event.place ?? '', event.city ?? '', countryName]
    .filter((part) => part !== '')
    .join(', ');

  return {
    id: event.id,
    organisationName: event.organisationName ?? '',
    name: event.name,
    when,
    startDate: event.startDate ?? '',
    endDate: event.endDate ?? '',
    placeLine,
    showFlag: event.countryCode === 'CH',
    flagAlt: countryName,
    attendance: event.attendance.map((item) => item.label).join(` ${and} `),
    attendanceType: event.attendance.map((term) => term.slug),
    access: event.access?.label ?? '',
    price: event.price?.slug ?? '',
    descriptionHtml: event.description ?? '',
    coverUrl: event.coverUrl,
    coverAlt: event.coverAlt ?? event.name,
    ctaUrl: event.ctaUrl,
    ctaLabel: event.ctaLabel,
    contactEmail: event.contactEmail,
    contactLabel: event.contactLabel,
    heading,
  };
};

const termOptions = (
  terms: { slug: string; label: string }[],
  lang: string,
): FilterOption[] => {
  const labels = new Map<string, string>();
  for (const term of terms) {
    if (!labels.has(term.slug)) {
      labels.set(term.slug, term.label);
    }
  }

  return [...labels.entries()]
    .map(([value, label]) => ({ value, label }))
    .sort((a, b) => a.label.localeCompare(b.label, lang));
};

const optionValues = (terms: { slug: string }[]): string[] => [
  ...new Set(terms.map((term) => term.slug)),
].sort();

const isRecord = (value: unknown): value is Record<string, unknown> =>
  value != null && typeof value === 'object' && !Array.isArray(value);
